import React from 'react';
import { Link } from 'react-router-dom';

import { usePaperDocument } from '../hooks/usePaperDocument';
import './paper.css';

const REPO = 'https://github.com/TheoTime01/Tello-drone-with-ROS2';
const VIDEO = 'https://youtu.be/YJ8u02rbd7Y';
const EMBED = 'https://www.youtube-nocookie.com/embed/YJ8u02rbd7Y';

/* Display equations are written as MathML so that they render natively in the
 * document font instead of pulling in a formula-typesetting library. React
 * renders MathML tags, but going through innerHTML keeps the markup verbatim
 * and keeps this file readable. */
const Equation = ({ tag, math }) => (
  <div className="eq">
    <div className="eq-body" dangerouslySetInnerHTML={{ __html: math }} />
    <div className="eq-tag">({tag})</div>
  </div>
);

const EQ_FOV = `
<math display="block">
  <msub><mi>&#x3B1;</mi><mi>h</mi></msub><mo>=</mo>
  <mn>2</mn><mspace width="0.15em"/><mi>arctan</mi><mo>&#x2061;</mo>
  <mfrac>
    <msub><mi>W</mi><mi>img</mi></msub>
    <mrow><mn>2</mn><msub><mi>f</mi><mi>x</mi></msub></mrow>
  </mfrac>
  <mo>,</mo><mspace width="1em"/>
  <msub><mi>&#x3B1;</mi><mi>v</mi></msub><mo>=</mo>
  <mn>2</mn><mspace width="0.15em"/><mi>arctan</mi><mo>&#x2061;</mo>
  <mfrac>
    <msub><mi>H</mi><mi>img</mi></msub>
    <mrow><mn>2</mn><msub><mi>f</mi><mi>y</mi></msub></mrow>
  </mfrac>
</math>`;

const EQ_BANDWIDTH = `
<math display="block">
  <mover><mi>B</mi><mo>&#x02D9;</mo></mover><mo>=</mo>
  <mn>3</mn><msub><mi>W</mi><mi>img</mi></msub><msub><mi>H</mi><mi>img</mi></msub><mi>f</mi>
  <mo>=</mo>
  <mn>3</mn><mo>&#x22C5;</mo><mn>960</mn><mo>&#x22C5;</mo><mn>720</mn><mo>&#x22C5;</mo><mn>30</mn>
  <mo>&#x2248;</mo>
  <mn>62.2</mn><mspace width="0.2em"/><mi>MB</mi><mo>/</mo><mi>s</mi>
</math>`;

const EQ_RANGE = `
<math display="block">
  <mi>Z</mi><mo>=</mo>
  <mfrac>
    <mrow><msub><mi>f</mi><mi>x</mi></msub><mspace width="0.15em"/><mi>W</mi></mrow>
    <mi>w</mi>
  </mfrac>
</math>`;

const EQ_ANGLE = `
<math display="block">
  <msub><mi>&#x3B5;</mi><mi>u</mi></msub><mo>=</mo>
  <mi>arctan</mi><mo>&#x2061;</mo>
  <mfrac>
    <mrow><mi>u</mi><mo>&#x2212;</mo><msub><mi>u</mi><mi>c</mi></msub></mrow>
    <msub><mi>f</mi><mi>x</mi></msub>
  </mfrac>
  <mo>&#x2248;</mo>
  <mfrac>
    <mrow><mi>u</mi><mo>&#x2212;</mo><msub><mi>u</mi><mi>c</mi></msub></mrow>
    <msub><mi>f</mi><mi>x</mi></msub>
  </mfrac>
  <mo>,</mo><mspace width="1em"/>
  <msub><mi>u</mi><mi>c</mi></msub><mo>=</mo>
  <mfrac><msub><mi>W</mi><mi>img</mi></msub><mn>2</mn></mfrac>
  <mo>&#x2260;</mo>
  <msub><mi>c</mi><mi>x</mi></msub>
</math>`;

const EQ_RELAY = `
<math display="block">
  <mtable>
    <mtr><mtd>
      <msub><mi>v</mi><mi>i</mi></msub><mo>=</mo>
      <msub><mi>V</mi><mi>i</mi></msub><mspace width="0.2em"/>
      <mi>sgn</mi><mo>&#x2061;</mo><mspace width="0.15em"/>
      <mrow><mo stretchy="false">(</mo><msub><mi>e</mi><mi>i</mi></msub><mo stretchy="false">)</mo></mrow>
      <mspace width="2em"/>
      <mtext>if&#xA0;</mtext>
      <mrow><mo>|</mo><msub><mi>e</mi><mi>i</mi></msub><mo>|</mo></mrow>
      <mo>&gt;</mo><msub><mi>&#x3B4;</mi><mi>i</mi></msub>
    </mtd></mtr>
    <mtr><mtd>
      <msub><mi>v</mi><mi>i</mi></msub><mo>=</mo><mn>0</mn>
      <mspace width="2em"/>
      <mtext>otherwise</mtext>
    </mtd></mtr>
  </mtable>
</math>`;

const EQ_DEADBAND = `
<math display="block">
  <mi>Z</mi><mo>&#x2208;</mo>
  <mrow><mo stretchy="true">[</mo>
    <mfrac>
      <mrow><msub><mi>f</mi><mi>x</mi></msub><mi>W</mi></mrow>
      <mrow><msub><mi>w</mi><mn>0</mn></msub><mo>+</mo><mi>&#x394;</mi></mrow>
    </mfrac>
    <mo>,</mo><mspace width="0.4em"/>
    <mfrac>
      <mrow><msub><mi>f</mi><mi>x</mi></msub><mi>W</mi></mrow>
      <mrow><msub><mi>w</mi><mn>0</mn></msub><mo>&#x2212;</mo><mi>&#x394;</mi></mrow>
    </mfrac>
  <mo stretchy="true">]</mo></mrow>
  <mo>=</mo>
  <mrow><mo stretchy="true">[</mo>
    <mn>0.78</mn><mspace width="0.15em"/><msub><mi>Z</mi><mn>0</mn></msub>
    <mo>,</mo><mspace width="0.4em"/>
    <mn>1.40</mn><mspace width="0.15em"/><msub><mi>Z</mi><mn>0</mn></msub>
  <mo stretchy="true">]</mo></mrow>
</math>`;

const EQ_BBOX = `
<math display="block">
  <msub><mi>w</mi><mi>bb</mi></msub><mo stretchy="true">(</mo><mi>&#x3B8;</mi><mo stretchy="true">)</mo>
  <mo>=</mo>
  <mi>w</mi><mrow><mo stretchy="true">(</mo>
    <mrow><mo>|</mo><mi>cos</mi><mo>&#x2061;</mo><mi>&#x3B8;</mi><mo>|</mo></mrow>
    <mo>+</mo>
    <mrow><mo>|</mo><mi>sin</mi><mo>&#x2061;</mo><mi>&#x3B8;</mi><mo>|</mo></mrow>
  <mo stretchy="true">)</mo></mrow>
  <mspace width="1.5em"/>
  <mo>&#x27F9;</mo>
  <mspace width="1.5em"/>
  <mover><mi>Z</mi><mo>^</mo></mover>
  <mo>&#x2265;</mo>
  <mfrac><mi>Z</mi><msqrt><mn>2</mn></msqrt></mfrac>
  <mo>=</mo><mn>0.707</mn><mspace width="0.15em"/><mi>Z</mi>
</math>`;

const TelloProject = () => {
  const { theme, toggleTheme } = usePaperDocument('/projects/tello');

  return (
    <article className="paper-doc">
      <div className="doc-bar">
        <Link to="/">&larr; Théotime Perrichet</Link>
        <button type="button" onClick={toggleTheme}>
          {theme === 'dark' ? ': Light :' : ': Dark :'}
        </button>
      </div>

      <h1>
        A ROS 2 behaviour stack for the Tello EDU: monocular marker servoing,
        teleoperation and command arbitration
      </h1>

      <p className="author">
        Théotime Perrichet &nbsp;&middot;&nbsp; Tom Reche &nbsp;&middot;&nbsp; Arnaud Sibenaler
      </p>
      <p className="doc-affiliation">Robotic systems integration project, M.Eng. Robotics, CPE Lyon</p>
      <p className="doc-dateline">Lyon, France · October–November 2024</p>

      <ul className="doc-links">
        <li><a href={REPO}>Source code</a></li>
        <li><a href={VIDEO}>Demonstration video</a></li>
      </ul>

      <div className="abstract">
        <h2>Abstract</h2>
        <p>
          This report documents a ROS&nbsp;2 Humble control stack for the Ryze
          DJI Tello&nbsp;EDU, a palm-sized Wi-Fi quadrotor whose only
          exteroceptive sensor is a forward monocular camera. Four flight behaviours,
          namely joystick teleoperation, monocular servoing onto a QR marker,
          a stationary yaw sweep, and a marker-triggered lateral traverse,
          are implemented as independent ROS&nbsp;2 nodes and multiplexed onto
          the single velocity channel the drone&rsquo;s SDK exposes by a gating
          arbiter node. The write-up reconstructs the geometry the servo loop
          actually implements: a three-level relay on the pixel coordinates of
          the marker&rsquo;s axis-aligned bounding box, with range inferred from
          apparent width through the pinhole model. From the platform&rsquo;s
          measured intrinsics we derive its 55.1&deg;&nbsp;&times;&nbsp;43.1&deg;
          field of view, a &plusmn;3.1&deg; angular dead band, a range dead band
          that is asymmetric by construction (&minus;22&nbsp;% / +40&nbsp;% of
          nominal standoff), and a 1.3&deg;&nbsp;/&nbsp;2.3&deg; static bias
          introduced by servoing to the frame centre rather than to the
          principal point. We then give a root-cause account of the one
          reproducible defect the project reports, a mode transition after
          which the aircraft keeps yawing, and show it follows necessarily
          from combining event-driven command publishers with a purely
          forwarding arbiter and a latching velocity interface.
        </p>
        <p className="keywords">
          <strong>Keywords:</strong> UAV · monocular visual servoing · fiducial
          markers · ROS 2 · command arbitration · camera calibration · relay control
        </p>
      </div>

      <nav aria-label="Table of contents">
        <ol>
          <li><a href="#s1">Introduction</a></li>
          <li><a href="#s2">Platform and interfaces</a></li>
          <li><a href="#s3">Software architecture</a></li>
          <li><a href="#s4">Camera model</a></li>
          <li><a href="#s5">Monocular marker servoing</a></li>
          <li><a href="#s6">Behaviour modes and mode switching</a></li>
          <li><a href="#s7">Failure analysis</a></li>
          <li><a href="#s8">Results</a></li>
          <li><a href="#s9">Limitations and further work</a></li>
          <li><a href="#s10">Reproducing the system</a></li>
        </ol>
      </nav>

      <h2 id="s1">1. Introduction</h2>
      <p>
        Small consumer quadrotors are attractive teaching platforms for the same
        reason they are awkward research platforms: the flight controller is
        closed. The Ryze DJI Tello&nbsp;EDU <a href="#r1">[1]</a> accepts
        velocity commands over a documented text protocol <a href="#r2">[2]</a>
        and returns a video stream and a state packet, but it exposes no
        actuator interface, no rate loop, and no position estimate. Whatever
        autonomy is added has to be built entirely in the outer loop, on a
        laptop, across a single Wi-Fi link, on top of a velocity interface whose
        internal dynamics are undocumented.
      </p>
      <p>
        The objective set for this project was to integrate that platform into
        ROS&nbsp;2 Humble and to implement four behaviours on it: manual flight
        from an Xbox joystick, servoing onto a QR marker, a stationary
        surveillance sweep, and a marker-triggered lateral traverse between a
        <em> start</em> and a <em>finish</em> code. The engineering interest is
        not in any one of those behaviours, which are individually simple, but
        in what happens where they meet: four asynchronous producers, one
        latching velocity consumer, and no state estimate to fall back on.
      </p>
      <p>
        This document reconstructs the system as built. Section&nbsp;3 describes
        the node graph and the arbitration scheme; §4 works through the camera
        calibration shipped with the driver and what it implies for the control
        loop; §5 derives the geometry of the servo law from its pixel-space
        parameters; §7 analyses the failure modes, including the one the
        project itself reports as unresolved. Every parameter quoted is read
        from the repository <a href="#r9">[9]</a> rather than re-derived, and
        every derived figure is marked as such.
      </p>

      <figure>
        <div className="video-frame">
          <iframe
            src={EMBED}
            title="Tello EDU with ROS 2: demonstration video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            loading="lazy"
          />
        </div>
        <figcaption>
          Demonstration video: joystick teleoperation with flips, the marker
          servoing loop holding station on a QR code, and the
          <em> travelling</em> traverse between markers. If the embed does not
          load, the video is on <a href={VIDEO}>YouTube</a>.
        </figcaption>
      </figure>

      <h2 id="s2">2. Platform and interfaces</h2>

      <h3>2.1 Airframe and command axes</h3>
      <p>
        The Tello&nbsp;EDU is a four-rotor aircraft in the conventional cross
        configuration: two rotors turn clockwise and two counter-clockwise, so
        that thrust, roll, pitch and yaw moments are produced by differential
        rotor speeds. Collective thrust sets altitude, roll and pitch tilt the
        thrust vector to produce lateral and longitudinal acceleration, and the
        residual reaction torque from the counter-rotating pairs produces yaw.
        The four commanded degrees of freedom are therefore three body
        velocities plus a yaw rate; the aircraft is underactuated, and lateral
        motion is only available through attitude.
      </p>

      <figure>
        <div className="fig-plate">
          <img
            src="/images/tello/body-axes.jpg"
            alt="Diagram of the Tello body frame: X forward (pitch), Y left (roll), Z up (yaw and throttle)"
          />
        </div>
        <figcaption>
          Body-frame convention as defined in the project documentation:
          <em> x</em> forward (pitch), <em>y</em> left (roll), <em>z</em> up
          (throttle and yaw). This is the ROS convention of REP&nbsp;103
          <a href="#r7">[7]</a>; §7.3 shows that the code does not follow it.
        </figcaption>
      </figure>

      <p>
        Everything below the velocity interface, meaning mixing, rate control,
        attitude stabilisation, and the optical-flow and time-of-flight sensors
        used for hold, is inside the manufacturer&rsquo;s firmware and is neither
        observable nor tunable. The aircraft is best modelled here as a velocity
        servo of unknown bandwidth, and the outer loop must be designed
        conservatively enough that this ignorance does not matter.
      </p>

      <h3>2.2 Link and interfaces</h3>
      <p>
        The drone raises its own 2.4&nbsp;GHz access point and answers at{' '}
        <code>192.168.10.1</code>, the default of the{' '}
        <code>tello_ip</code> parameter. Three UDP services run over that single
        link: the command and response channel, a periodic state broadcast, and an
        H.264 video stream. The driver reaches them through{' '}
        <code>djitellopy</code> <a href="#r3">[3]</a>, which decodes the video
        with PyAV and exposes the state fields as accessors.
      </p>
      <p>
        Two consequences follow and constrain everything else. First, the
        control station must associate with the drone&rsquo;s access point, so
        one laptop flies one drone and has no other network while doing so.
        Second, command, telemetry and video contend for the same radio: video
        loss and command latency are correlated, which is exactly the wrong
        correlation for a vision-in-the-loop controller, since the moments when
        the perception input degrades are the moments when corrective commands
        are slowest to arrive.
      </p>
      <p>
        The velocity interface itself is the SDK&rsquo;s{' '}
        <code>rc a b c d</code> command, wrapped by{' '}
        <code>send_rc_control</code>. Its four arguments are dimensionless
        integers on <math><mrow><mo>[</mo><mo>&#x2212;</mo><mn>100</mn><mo>,</mo><mn>100</mn><mo>]</mo></mrow></math>{' '}
        (left/right, forward/backward, up/down and yaw) and, critically for
        §7, the command <em>latches</em>: the aircraft holds the last value it
        received until a new one arrives or the firmware&rsquo;s command timeout
        expires. There is no periodic heartbeat requirement at the application
        layer, and correspondingly no application-layer guarantee that a stale
        command will ever be revised.
      </p>

      <h2 id="s3">3. Software architecture</h2>

      <h3>3.1 Packages and provenance</h3>
      <p>
        The workspace contains five packages, of which two are the work of this
        project and three are vendored:
      </p>

      <div className="scroll-wrapper">
        <table>
          <thead>
            <tr>
              <th>Package</th>
              <th>Build type</th>
              <th>Origin</th>
              <th>Role</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>drone_control</code></td>
              <td>ament_python</td>
              <td>this project</td>
              <td>Behaviour nodes and the arbiter</td>
            </tr>
            <tr>
              <td><code>tello_control_interface</code></td>
              <td>ament_cmake</td>
              <td>this project</td>
              <td><code>DroneMode</code> service definition</td>
            </tr>
            <tr>
              <td><code>tello</code></td>
              <td>ament_python</td>
              <td>tentone <a href="#r4">[4]</a></td>
              <td>SDK driver: video, telemetry, command</td>
            </tr>
            <tr>
              <td><code>tello_msg</code></td>
              <td>ament_cmake</td>
              <td>tentone <a href="#r4">[4]</a></td>
              <td>Status, identity and Wi-Fi messages</td>
            </tr>
            <tr>
              <td><code>tello_control</code></td>
              <td>ament_cmake</td>
              <td>tentone <a href="#r4">[4]</a></td>
              <td>C++ keyboard teleoperation (unused at runtime)</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        Marker decoding is likewise off the shelf: <code>zbar_ros</code>{' '}
        <a href="#r5">[5]</a> for the string-triggered behaviours, and{' '}
        <code>pyzbar</code> <a href="#r6">[6]</a> called directly inside the
        servoing node. Both wrap the ZBar library. The contribution of this
        project is therefore the behaviour layer and its arbitration, not the
        driver or the decoder, which is the correct division of labour for a
        four-week integration exercise, and is stated here so that the analysis
        below is read as analysis of an integration, not of a bare-metal
        implementation.
      </p>

      <h3>3.2 Runtime graph</h3>
      <p>
        The launch file starts seven nodes, and topic remapping, rather than
        conditional logic inside the nodes, is what separates the four command
        sources. Each behaviour node publishes on a topic called{' '}
        <code>/control</code> in its own namespace, and the launch file remaps
        that name to a distinct topic per node:
      </p>

      <pre><code>{`Node(package='drone_control', executable='control',
     remappings=[('/control', '/secure_cmd')]),
Node(package='drone_control', executable='qr_code_follower',
     remappings=[('/control', '/control_qr')]),
Node(package='drone_control', executable='travelling',
     remappings=[('/control', '/control_travelling')]),
Node(package='drone_control', executable='monitoring_mode',
     remappings=[('/control', '/control_monitoring')]),`}</code></pre>

      <p>
        The behaviour nodes are thus written as if each owned the aircraft, and
        are demoted to candidates at launch time. This is a clean pattern: a
        node can be run alone for bench testing, with no code change, simply by
        omitting its remapping.
      </p>

      <figure>
        <div className="fig-wide">
          <img
            src="/images/tello/rosgraph.png"
            alt="rqt_graph capture of the running ROS 2 node graph, showing the four command sources feeding the behaviour arbiter and the arbiter feeding the tello driver"
          />
        </div>
        <figcaption>
          The runtime graph, captured with <code>rqt_graph</code> on the flying
          system. The four candidate command topics
          (<code>/secure_cmd</code>, <code>/control_qr</code>,{' '}
          <code>/control_monitoring</code> and <code>/control_travelling</code>)
          converge on <code>/behavior</code>, which publishes the single{' '}
          <code>/control</code> topic consumed by <code>/tello</code>. The{' '}
          <code>/image</code> topic feeds two independent decoders. Note that{' '}
          <code>/behavior</code> is present in the running graph but absent from
          the launch file, so it is started by hand; the unconnected node on the
          left is the graph viewer itself.
        </figcaption>
      </figure>

      <h3>3.3 Command arbitration</h3>
      <p>
        The arbiter (<code>tello_behavior</code>, running as{' '}
        <code>/behavior</code>) holds one integer of state, the active mode{' '}
        <math><mrow><mi>m</mi><mo>&#x2208;</mo><mo>{'{'}</mo><mn>0</mn><mo>,</mo><mn>1</mn><mo>,</mo><mn>2</mn><mo>,</mo><mn>3</mn><mo>}</mo></mrow></math>,
        and subscribes to all four candidate topics. Each callback forwards its
        message to <code>/control</code> if and only if its own index equals{' '}
        <math><mi>m</mi></math>, and drops it otherwise. There is no priority
        ordering, no timeout, and no action taken on the transition itself: the
        arbiter is a pure gate, driven entirely by the arrival of messages on
        the selected input.
      </p>
      <p>
        That last property is the whole of §7.1. A gate is a sound arbitration
        primitive when every input is periodic and every input has a defined
        idle value; it is unsound when some inputs are event-driven, because
        then selecting an input that happens to be silent is indistinguishable
        from selecting no input at all, and the consumer latches.
      </p>

      <h3>3.4 Cadences</h3>
      <p>
        The rates below are the nominal ones set in code; none were measured in
        flight.
      </p>

      <div className="scroll-wrapper">
        <table>
          <thead>
            <tr>
              <th>Topic</th>
              <th>Publisher</th>
              <th className="num">Rate</th>
              <th>Trigger</th>
              <th>Idle value</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>/image</code></td>
              <td><code>tello</code></td>
              <td className="num">30 Hz</td>
              <td>fixed sleep</td>
              <td>n/a</td>
            </tr>
            <tr>
              <td><code>/imu</code>, <code>/odom</code></td>
              <td><code>tello</code></td>
              <td className="num">10 Hz</td>
              <td>fixed sleep</td>
              <td>n/a</td>
            </tr>
            <tr>
              <td><code>/status</code>, <code>/battery</code>, <code>/id</code></td>
              <td><code>tello</code></td>
              <td className="num">2 Hz</td>
              <td>fixed sleep</td>
              <td>n/a</td>
            </tr>
            <tr>
              <td><code>/secure_cmd</code></td>
              <td><code>control</code></td>
              <td className="num">event</td>
              <td><code>/joy</code> message</td>
              <td>zero (sticks centred)</td>
            </tr>
            <tr>
              <td><code>/control_qr</code></td>
              <td><code>qr_code_follower</code></td>
              <td className="num">≈30 Hz</td>
              <td><code>/image</code> message</td>
              <td>zero (no marker)</td>
            </tr>
            <tr>
              <td><code>/control_monitoring</code></td>
              <td><code>monitoring_mode</code></td>
              <td className="num">1 Hz</td>
              <td>wall timer</td>
              <td><em>none</em>; always yaw = 50</td>
            </tr>
            <tr>
              <td><code>/control_travelling</code></td>
              <td><code>travelling</code></td>
              <td className="num">event</td>
              <td><code>/barcode</code> message</td>
              <td><em>none</em>; silent between markers</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        The two rightmost columns are the ones that matter. Two of the four
        sources publish continuously and emit an unambiguous zero when they have
        nothing to say; the other two do not. That asymmetry is invisible in the
        node graph and is the proximate cause of the defect in §7.1.
      </p>

      <h3>3.5 The cost of raw image transport</h3>
      <p>
        The driver publishes <code>sensor_msgs/Image</code> in{' '}
        <code>bgr8</code>, uncompressed, at 960&nbsp;&times;&nbsp;720 and
        30&nbsp;Hz. Each message is 2.07&nbsp;MB and the stream is
      </p>

      <Equation tag="1" math={EQ_BANDWIDTH} />

      <p>
        or roughly 500&nbsp;Mbit/s of intra-host traffic, to carry a video feed
        that arrives over the radio as a few Mbit/s of H.264. Two independent
        subscribers, <code>qr_code_follower</code> and{' '}
        <code>zbar_ros</code>, each take a copy, and each runs its own ZBar
        decode over the same pixels. Whether the second copy costs a second
        serialisation depends on whether the DDS implementation&rsquo;s
        shared-memory transport is active, which was not checked. The
        conventional remedies are to publish a compressed transport, or to
        compose the decoder and the driver into one process so that the image is
        passed by pointer, or simply to decode once and publish the marker pose
        rather than the frame. None was needed at this scale, but the margin is
        smaller than it looks, since this traffic shares a laptop with the
        H.264 decoder.
      </p>

      <h2 id="s4">4. Camera model</h2>

      <h3>4.1 Intrinsics</h3>
      <p>
        The driver ships a calibration for the Tello&rsquo;s forward camera,
        obtained with the standard ROS checkerboard procedure and stored as a{' '}
        <code>plumb_bob</code> model. The parameters below are read verbatim
        from <code>tello/resource/ost.yaml</code>.
      </p>

      <div className="scroll-wrapper">
        <table>
          <thead>
            <tr><th>Quantity</th><th className="num">Value</th><th>Note</th></tr>
          </thead>
          <tbody>
            <tr><td>Image size</td><td className="num">960 &times; 720</td><td>px</td></tr>
            <tr><td><math><msub><mi>f</mi><mi>x</mi></msub></math></td><td className="num">919.425</td><td>px</td></tr>
            <tr><td><math><msub><mi>f</mi><mi>y</mi></msub></math></td><td className="num">911.926</td><td>px; 0.8 % from <math><msub><mi>f</mi><mi>x</mi></msub></math></td></tr>
            <tr><td><math><msub><mi>c</mi><mi>x</mi></msub></math></td><td className="num">459.656</td><td>px; 20.3 px left of image centre</td></tr>
            <tr><td><math><msub><mi>c</mi><mi>y</mi></msub></math></td><td className="num">323.552</td><td>px; 36.4 px above image centre</td></tr>
            <tr><td><math><msub><mi>k</mi><mn>1</mn></msub></math></td><td className="num">&minus;0.041948</td><td>radial, barrel</td></tr>
            <tr><td><math><msub><mi>k</mi><mn>2</mn></msub></math></td><td className="num">+0.048619</td><td>radial</td></tr>
            <tr><td><math><msub><mi>p</mi><mn>1</mn></msub></math></td><td className="num">&minus;0.022789</td><td>tangential</td></tr>
            <tr><td><math><msub><mi>p</mi><mn>2</mn></msub></math></td><td className="num">&minus;0.004038</td><td>tangential</td></tr>
          </tbody>
        </table>
      </div>

      <h3>4.2 Field of view</h3>
      <p>
        From the focal lengths and the sensor extent,
      </p>

      <Equation tag="2" math={EQ_FOV} />

      <p>
        which evaluates to 55.1&deg; horizontally and 43.1&deg; vertically. This
        is the single most consequential number in the system: it is the entire
        exteroceptive aperture of the aircraft. Outside a 55&deg; forward cone
        the drone is blind, and since the servo loop has no memory of a marker
        it has lost, with no filter and no last-known-bearing search, leaving that
        cone is unrecoverable without operator intervention.
      </p>

      <h3>4.3 Distortion budget</h3>
      <p>
        The distortion coefficients are worth a second look, because their
        relative sizes are unusual. Evaluating the <code>plumb_bob</code> model
        at the image corner farthest from the principal point gives a radial
        displacement of about 5&nbsp;px, and a tangential displacement of about
        (&minus;10.7,&nbsp;&minus;14.4)&nbsp;px, i.e. 17.9&nbsp;px in
        magnitude.<a href="#f1" id="f1-ref">&nbsp;1</a> Tangential terms
        dominating the radial ones by a factor of three, and reaching
        2&nbsp;&times;&nbsp;10<sup>&minus;2</sup> in normalised units, is not
        what a well-conditioned calibration of a small fixed-focus lens looks
        like; together with the 36&nbsp;px offset of{' '}
        <math><msub><mi>c</mi><mi>y</mi></msub></math> from the image centre, it
        suggests a fit with too few views or insufficient coverage of the frame
        corners, rather than genuine decentering of that magnitude.
      </p>
      <p>
        The practical consequence is nil, and it is worth saying why: the servo
        law of §5 has a &plusmn;50&nbsp;px dead band, and the worst-case
        distortion displacement is 18&nbsp;px, a third of it, in the corners,
        where the marker never sits at equilibrium. A relay controller with a
        dead band this wide is insensitive to a calibration this rough. The
        conclusion is not that the calibration is good, but that the loop was
        designed loosely enough not to need it.
      </p>

      <h3>4.4 The calibration is not in the loop</h3>
      <p>
        The calibration is published on <code>/camera_info</code> and then never
        consumed. No node subscribes to it; no image is rectified; the servo
        loop operates directly on the distorted frame. More importantly, the
        loop takes its reference point from the array dimensions rather than
        from the calibration:
      </p>

      <pre><code>{`frame_center_x = frame.shape[1] // 2   # 480, not c_x = 459.7
frame_center_y = frame.shape[0] // 2   # 360, not c_y = 323.6`}</code></pre>

      <Equation tag="3" math={EQ_ANGLE} />

      <p>
        The controller therefore drives the marker to the geometric centre of
        the array, not to the optical axis. The two differ by 20.3&nbsp;px
        horizontally and 36.4&nbsp;px vertically, which through the focal lengths
        is a fixed pointing bias of 1.27&deg; and 2.29&deg;. At the nominal
        standoff derived in §5.4 for a 100&nbsp;mm marker (1.31&nbsp;m), the
        aircraft settles about 29&nbsp;mm to one side and 53&nbsp;mm above true
        bore-sight alignment. This is a systematic error, not noise: it does not
        average out, and it is invisible in flight because the dead band is
        wider than the bias. Replacing two integer divisions with the values
        already being published on <code>/camera_info</code> would remove it.
      </p>

      <h2 id="s5">5. Monocular marker servoing</h2>

      <h3>5.1 Markers</h3>
      <p>
        Six QR markers are defined, encoding the strings{' '}
        <code>start</code>, <code>stop</code>, <code>finish</code>,{' '}
        <code>drop_area</code>, <code>blue_block</code> and{' '}
        <code>red_block</code>; the last three are reserved and trigger nothing.
        All six are 21&nbsp;&times;&nbsp;21 modules, i.e. QR version&nbsp;1
        <a href="#r8">[8]</a>, the smallest symbol in the standard, which is the
        right choice here, since module count is what sets the range at which
        the code stops decoding (§5.6).
      </p>

      <figure>
        <div className="fig-row">
          <div>
            <img src="/images/tello/qr-start.png" alt="QR marker encoding the string start" />
            <span className="fig-label"><code>start</code></span>
          </div>
          <div>
            <img src="/images/tello/qr-finish.png" alt="QR marker encoding the string finish" />
            <span className="fig-label"><code>finish</code></span>
          </div>
          <div>
            <img src="/images/tello/qr-stop.png" alt="QR marker encoding the string stop" />
            <span className="fig-label"><code>stop</code></span>
          </div>
        </div>
        <figcaption>
          The three active markers, <code>start</code>, <code>finish</code> and{' '}
          <code>stop</code>, each a 21&nbsp;&times;&nbsp;21-module version-1
          symbol. The remaining three (<code>drop_area</code>,{' '}
          <code>blue_block</code>, <code>red_block</code>) are defined but bound
          to no behaviour.
        </figcaption>
      </figure>

      <h3>5.2 Range from apparent width</h3>
      <p>
        The servoing node has no depth sensor and does not solve for the
        marker&rsquo;s pose. It uses the one scalar the decoder gives it for
        free, the width <math><mi>w</mi></math> in pixels of the
        axis-aligned bounding box, and inverts the pinhole projection of a
        planar target of physical side <math><mi>W</mi></math> held normal to
        the optical axis:
      </p>

      <Equation tag="4" math={EQ_RANGE} />

      <p>
        This is the cheapest possible range cue and it is entirely adequate for
        station-keeping, provided one is honest about what it assumes: a known
        marker size, a fronto-parallel target, and no in-plane rotation. §5.5
        quantifies what each assumption costs when it is violated.
      </p>

      <h3>5.3 The control law</h3>
      <p>
        Each of the three translational channels is an independent three-level
        relay with a dead zone, acting on a pixel error{' '}
        <math><msub><mi>e</mi><mi>i</mi></msub></math>, with no proportional
        term, no integrator and no rate limit. Every channel has the same form,
        differing only in its threshold{' '}
        <math><msub><mi>&#x3B4;</mi><mi>i</mi></msub></math> and its output
        magnitude <math><msub><mi>V</mi><mi>i</mi></msub></math>, given in SDK
        velocity units:
      </p>

      <Equation tag="5" math={EQ_RELAY} />

      <p>
        The lateral and vertical channels take{' '}
        <math><msub><mi>e</mi><mi>i</mi></msub></math> as the offset of the
        marker centre from the frame centre, with{' '}
        <math><mrow><msub><mi>&#x3B4;</mi><mi>i</mi></msub><mo>=</mo><mn>50</mn></mrow></math>{' '}
        px; the range channel takes it as the departure of the bounding-box
        width from a 70&nbsp;px setpoint, with{' '}
        <math><mrow><msub><mi>&#x3B4;</mi><mi>i</mi></msub><mo>=</mo><mn>20</mn></mrow></math>{' '}
        px. The yaw channel is not driven at all: the aircraft translates to
        track the marker and never rotates to face it, so a marker that leaves
        the frame sideways is followed by a sideways translation rather than by
        a turn.
      </p>

      <div className="scroll-wrapper">
        <table>
          <thead>
            <tr>
              <th>Channel</th>
              <th>Error signal</th>
              <th className="num">Setpoint</th>
              <th className="num">Dead band</th>
              <th className="num">Output</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Lateral (<code>linear.x</code>)</td>
              <td>marker centre <math><mi>u</mi></math></td>
              <td className="num">480 px</td>
              <td className="num">&plusmn;50 px</td>
              <td className="num">&plusmn;20</td>
            </tr>
            <tr>
              <td>Vertical (<code>linear.z</code>)</td>
              <td>marker centre <math><mi>v</mi></math></td>
              <td className="num">360 px</td>
              <td className="num">&plusmn;50 px</td>
              <td className="num">&plusmn;35</td>
            </tr>
            <tr>
              <td>Range (<code>linear.y</code>)</td>
              <td>bbox width <math><mi>w</mi></math></td>
              <td className="num">70 px</td>
              <td className="num">&plusmn;20 px</td>
              <td className="num">&plusmn;25</td>
            </tr>
            <tr>
              <td>Yaw (<code>angular.z</code>)</td>
              <td>n/a</td>
              <td className="num">n/a</td>
              <td className="num">n/a</td>
              <td className="num">0</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        A relay with a dead band is the classical way to get acceptable
        behaviour out of a plant whose gain is unknown, which is exactly the
        situation here: nothing in the system knows how many metres per second
        an SDK velocity unit is worth. The price is a limit cycle. With a plant
        of unknown phase lag driven by a discontinuous law at roughly 30&nbsp;Hz
        over a radio link, the equilibrium is not a point but an oscillation
        whose amplitude is set by the dead band and whose period is set by the
        loop delay. Neither was measured, and measuring them with a step response
        and a bounded-oscillation test is the first thing this system needs
        before any gain is touched.
      </p>

      <h3>5.4 Dead-band geometry</h3>
      <p>
        Both dead bands are specified in pixels, which makes them
        state-dependent once mapped back to the world. The angular ones are
        benign: &plusmn;50&nbsp;px through the focal lengths is
        &plusmn;3.11&deg; horizontally and &plusmn;3.14&deg; vertically,
        constant with range, which is a well-behaved specification. The range
        dead band is not, because <math><mi>Z</mi></math> is inversely
        proportional to <math><mi>w</mi></math>:
      </p>

      <Equation tag="6" math={EQ_DEADBAND} />

      <p>
        with <math><mrow><msub><mi>w</mi><mn>0</mn></msub><mo>=</mo><mn>70</mn></mrow></math>{' '}
        px and <math><mrow><mi>&#x394;</mi><mo>=</mo><mn>20</mn></mrow></math> px.
        A symmetric &plusmn;20&nbsp;px window in image space is a &minus;22&nbsp;%
        / +40&nbsp;% window in range: the aircraft tolerates nearly twice as much
        error moving away as moving toward. That asymmetry is inherited from the
        projection, not chosen, and it points the wrong way for a vehicle whose
        failure mode at close range is a collision and whose failure mode at long
        range is a lost lock. Specifying the dead band in range and converting to
        pixels per frame would cost one division.
      </p>
      <p>
        The absolute standoff depends on the printed marker size, which the
        repository does not record. For the physical sizes plausible for a
        hand-held A-series print:
      </p>

      <div className="scroll-wrapper">
        <table>
          <thead>
            <tr>
              <th>Marker side <math><mi>W</mi></math></th>
              <th className="num">Near edge (<math><mrow><mi>w</mi><mo>=</mo><mn>90</mn></mrow></math>)</th>
              <th className="num">Setpoint (<math><mrow><mi>w</mi><mo>=</mo><mn>70</mn></mrow></math>)</th>
              <th className="num">Far edge (<math><mrow><mi>w</mi><mo>=</mo><mn>50</mn></mrow></math>)</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>60 mm</td><td className="num">0.61 m</td><td className="num">0.79 m</td><td className="num">1.10 m</td></tr>
            <tr><td>80 mm</td><td className="num">0.82 m</td><td className="num">1.05 m</td><td className="num">1.47 m</td></tr>
            <tr><td>100 mm</td><td className="num">1.02 m</td><td className="num">1.31 m</td><td className="num">1.84 m</td></tr>
            <tr><td>150 mm</td><td className="num">1.53 m</td><td className="num">1.97 m</td><td className="num">2.76 m</td></tr>
          </tbody>
        </table>
      </div>

      <p>
        At the 100&nbsp;mm row, the lateral dead band of &plusmn;3.11&deg;
        corresponds to &plusmn;71&nbsp;mm of allowed lateral displacement, a
        sensible tolerance for a hand-held target, and evidence that the pixel
        thresholds were tuned empirically against a marker of about that size.
      </p>

      <h3>5.5 Systematic biases in the range estimate</h3>
      <p>
        Equation&nbsp;(4) uses the width of the <em>axis-aligned</em> bounding
        box returned by the decoder, not the side length of the symbol. Two
        distinct geometric effects follow, and they push in opposite directions.
      </p>
      <p>
        <strong>In-plane rotation.</strong> If the marker is rolled by{' '}
        <math><mi>&#x3B8;</mi></math> in the image plane, its axis-aligned box
        grows:
      </p>

      <Equation tag="7" math={EQ_BBOX} />

      <p>
        At 45&deg; the box is <math><msqrt><mn>2</mn></msqrt></math> times too
        wide, so the estimated range is 29&nbsp;% short and the controller backs
        away from a marker that has not moved. Since the drone never rotates to
        align with the marker (§5.3), and the operator holds the code by hand,
        this is a routine condition rather than a corner case.
      </p>
      <p>
        <strong>Out-of-plane rotation.</strong> If the marker is yawed by{' '}
        <math><mi>&#x3C8;</mi></math> about its vertical axis, its projected
        width foreshortens as{' '}
        <math><mrow><mi>cos</mi><mo>&#x2061;</mo><mi>&#x3C8;</mi></mrow></math>,
        so the estimated range is <math><mrow><mn>1</mn><mo>/</mo><mi>cos</mi><mo>&#x2061;</mo><mi>&#x3C8;</mi></mrow></math>{' '}
        too large and the aircraft closes in on a target it believes is farther
        than it is, at 30&deg; of yaw, by 15&nbsp;%. This is the dangerous sign
        of the two, since the error is toward the obstacle.
      </p>
      <p>
        Both vanish if the four corner points the decoder already returns are
        used instead of the bounding box: ZBar reports the symbol polygon, and a
        homography from the four corners of a square of known size yields full
        relative pose, which would also supply the missing yaw channel. That is
        the single highest-value change available to this loop, and it needs no
        new sensor and no new dependency.
      </p>

      <h3>5.6 Resolution floor</h3>
      <p>
        A QR symbol decodes only while its modules are resolved. At version&nbsp;1
        the symbol is 21 modules across, so the module pitch in pixels at the
        edges of the range dead band is
      </p>

      <div className="scroll-wrapper">
        <table>
          <thead>
            <tr><th>Bounding-box width</th><th className="num">Pixels per module</th><th>Position in dead band</th></tr>
          </thead>
          <tbody>
            <tr><td>90 px</td><td className="num">4.29</td><td>near edge</td></tr>
            <tr><td>70 px</td><td className="num">3.33</td><td>setpoint</td></tr>
            <tr><td>50 px</td><td className="num">2.38</td><td>far edge</td></tr>
          </tbody>
        </table>
      </div>

      <p>
        Decoders in this class need on the order of two to three pixels per
        module to recover a symbol reliably; this is an ordering argument from
        sampling, not a measured threshold for this decoder and this
        lens.<a href="#f2" id="f2-ref">&nbsp;2</a> Taken at face value it places
        the far edge of the dead band at or below the decoding floor, and the
        setpoint itself only modestly above it. The system should therefore be
        expected to lose lock intermittently while station-keeping, and
        especially while retreating, which is precisely the regime the
        asymmetric dead band of §5.4 makes it spend the most time in.
      </p>
      <p>
        What keeps this from being a safety problem is a deliberate property of
        the node: when no marker is decoded, all three position classifications
        retain their <code>&quot;center&quot;</code> default and the node
        publishes a zero twist. Loss of lock therefore commands a hover, not a
        continuation of the last motion. This is the correct fail-safe, it is the
        one place in the codebase where an idle value is defined explicitly, and
        §7.1 is about what happens in the parts of the system where it is not.
      </p>

      <h2 id="s6">6. Behaviour modes and mode switching</h2>

      <div className="scroll-wrapper">
        <table>
          <thead>
            <tr>
              <th className="num"><math><mi>m</mi></math></th>
              <th>Mode</th>
              <th>Node</th>
              <th>Command</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="num">0</td>
              <td>Manual</td>
              <td><code>control</code></td>
              <td>Joystick axes scaled to &plusmn;70</td>
            </tr>
            <tr>
              <td className="num">1</td>
              <td>Marker follower</td>
              <td><code>qr_code_follower</code></td>
              <td>Three-axis relay (§5.3)</td>
            </tr>
            <tr>
              <td className="num">2</td>
              <td>Monitoring</td>
              <td><code>monitoring_mode</code></td>
              <td>Constant yaw rate 50, 1 Hz</td>
            </tr>
            <tr>
              <td className="num">3</td>
              <td>Travelling</td>
              <td><code>travelling</code></td>
              <td>Lateral 20 on <code>start</code>, 0 on <code>finish</code></td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3>6.1 Manual</h3>
      <p>
        The teleoperation node maps the left stick to lateral and longitudinal
        velocity, the right stick to yaw rate and climb rate, all scaled to a
        maximum of 70 of the available 100, and the D-pad to the SDK&rsquo;s four
        flip primitives. <code>START</code> and <code>SELECT</code> arm takeoff
        and landing, guarded by a local latch; the <code>XBOX</code> button is a
        kill switch wired straight to the SDK <code>emergency</code> command,
        which cuts the motors.
      </p>
      <p>
        Two details are worth recording. The axis threshold is{' '}
        <math><mrow><mo>|</mo><mi>a</mi><mo>|</mo><mo>&gt;</mo><mn>0.01</mn></mrow></math>,
        one per cent of full scale, which is small enough that resting-stick noise
        passes through as a command of about one SDK unit, which is harmless
        but not a dead zone in any useful sense. And the flip handlers call{' '}
        <code>time.sleep(1)</code> inside the subscription callback, blocking the
        executor for a second: during a flip the node is deaf, including to the
        kill switch. Moving the flip to an action or a timer would make the
        emergency path preemptible, which is what one wants from a kill switch.
      </p>
      <p>
        A second teleoperation node, <code>control_plus_secure</code>, exists and
        is not launched. It fixes a real defect, in that it derives its takeoff
        latch from the <code>/takeoff</code> and <code>/land</code> topics rather
        than from a local variable, so the latch cannot desynchronise from the
        aircraft, but it also drops the sign inversions on the lateral and yaw
        axes that the launched node applies, so the two would fly mirrored. The
        fix and the regression are in the same file, which is why it is not the
        one running.
      </p>

      <h3>6.2 Monitoring</h3>
      <p>
        A timer publishes a constant yaw rate of 50 at 1&nbsp;Hz, spinning the
        aircraft in place to sweep the camera through 360&deg;. It is the
        simplest node in the system and the only one that never emits zero: the
        message it publishes is always a yaw command, which is what makes it the
        first half of the defect in §7.1.
      </p>

      <h3>6.3 Travelling</h3>
      <p>
        The traverse behaviour is a two-state machine driven by decoded marker
        strings on <code>/barcode</code>: <code>start</code> commands a lateral
        velocity of 20 and arms the state, <code>finish</code> commands zero and
        disarms it, and <code>stop</code> aborts. Each transition publishes
        exactly one message. Between markers the node is silent, relying on
        the SDK velocity command latching to keep the aircraft moving, which
        works, but means the drone is flying open-loop on a command issued
        seconds earlier with nothing periodically reaffirming it.
      </p>

      <h3>6.4 Mode switching</h3>
      <p>
        Mode changes go through a <code>DroneMode</code> service on{' '}
        <code>/drone_mode</code>, called from the teleoperation node&rsquo;s{' '}
        <code>LB</code> and <code>RB</code> shoulder buttons. The service field
        is named <code>mode</code> and its comment describes it as an absolute
        mode index, but the implementation reads it as a <em>direction</em>:
        zero decrements the active mode and anything else increments it, both
        with wraparound over the four modes. Selecting a specific mode therefore
        requires counting button presses, and the interface documentation and the
        implementation disagree about what the field means. Renaming the field to{' '}
        <code>step</code>, or implementing the absolute semantics the comment
        promises, would remove an ambiguity that costs nothing to fix and is
        exactly the sort that survives into a later integration.
      </p>

      <h2 id="s7">7. Failure analysis</h2>

      <h3>7.1 The monitoring-to-travelling transition</h3>
      <p>
        The project reports one reproducible defect: switching from{' '}
        <em>monitoring</em> to <em>travelling</em> leaves the aircraft spinning,
        although <em>travelling</em> behaves correctly when entered on its own.
        The three properties established above are jointly sufficient to explain
        it, and to show it is not a bug in either node.
      </p>
      <ol>
        <li>
          The SDK velocity command latches (§2.2): the aircraft holds the last
          value it received.
        </li>
        <li>
          The arbiter is a pure gate (§3.3): it acts only when a message arrives
          on the selected input, and does nothing on the transition itself.
        </li>
        <li>
          <em>Travelling</em> publishes only on marker events (§3.4): between
          markers it is silent.
        </li>
      </ol>
      <p>
        At the moment of the switch the last command forwarded to the aircraft
        was the monitoring node&rsquo;s yaw rate of 50. The arbiter changes{' '}
        <math><mi>m</mi></math> from 2 to 3 and immediately begins dropping the
        monitoring messages that keep arriving at 1&nbsp;Hz, but dropping a
        message is not the same as sending a zero. The newly selected input
        publishes nothing until a marker is decoded, so no message reaches{' '}
        <code>/control</code>, so no new <code>rc</code> command reaches the
        aircraft, so the yaw rate of 50 stands. The drone keeps spinning, exactly
        as reported, until either a marker happens to be decoded or the
        firmware&rsquo;s own command timeout expires. Entering <em>travelling</em>{' '}
        from a standstill works because the latched command was already zero.
      </p>
      <p>
        The defect is therefore in the arbitration contract, not in{' '}
        <code>monitoring_mode</code> or <code>travelling</code>. Two independent
        fixes each close it, and a robust system would have both: have the
        arbiter publish a zero twist on every mode change, so that a transition
        always produces a defined command; and give every behaviour node a fixed
        publication rate with an explicit idle value, as the marker follower
        already has, so that selecting an input can never mean selecting silence.
        The second is the standard command-multiplexer contract, a rate, a
        timeout and a defined idle output per input, as implemented by{' '}
        <code>twist_mux</code> <a href="#r10">[10]</a>, and adopting it would
        also close §7.2.
      </p>

      <h3>7.2 No watchdog on the command path</h3>
      <p>
        The same latching that causes §7.1 makes every node in the command chain
        safety-critical in a way none of them is written to be. If the marker
        follower crashes, or the executor blocks, or the Wi-Fi link drops while a
        non-zero command is in force, the aircraft continues executing that
        command. Nothing in the ROS graph notices; the only backstop is the
        firmware&rsquo;s command timeout, whose duration is a property of the
        closed system rather than a design parameter of this one.
      </p>
      <p>
        A watchdog node subscribing to <code>/control</code> and publishing a
        zero twist, or a landing command, after a fixed silence would bound
        this, and is perhaps twenty lines. For a vehicle that is airborne, has no
        position estimate and cannot be recovered by releasing a stick, it is not
        optional.
      </p>

      <h3>7.3 Frame conventions</h3>
      <p>
        The project documents its body frame as <em>x</em> forward, <em>y</em>{' '}
        left, <em>z</em> up, which is REP&nbsp;103 <a href="#r7">[7]</a>, the ROS
        standard, reproduced in the figure of §2.1. The code does not implement
        it. The driver&rsquo;s command callback forwards the fields positionally
        into the SDK call:
      </p>

      <pre><code>{`self.tello.send_rc_control(int(msg.linear.x),   # left / right
                           int(msg.linear.y),   # forward / backward
                           int(msg.linear.z),   # up / down
                           int(msg.angular.z))  # yaw`}</code></pre>

      <p>
        so <code>linear.x</code> is the <em>lateral</em> channel and{' '}
        <code>linear.y</code> the <em>longitudinal</em> one, transposed with
        respect to both REP&nbsp;103 and the project&rsquo;s own figure. The
        magnitudes are equally non-conforming: REP&nbsp;103 specifies metres per
        second, while these are dimensionless SDK percentages.
      </p>
      <p>
        Internally this is consistent and the aircraft flies correctly, because
        every producer in the workspace was written against the same
        transposition. It becomes a defect at the boundary: any stock ROS node
        emitting a <code>geometry_msgs/Twist</code>, be it a keyboard teleoperation
        node, a path follower or a Nav2 controller, will command the drone
        sideways when it means forward, and by a factor it cannot know. The fix
        is one adapter in the driver, converting a REP&nbsp;103 twist in SI units
        into SDK channels, and it is worth doing before anything upstream is
        connected.
      </p>

      <h3>7.4 Latent defects in the driver</h3>
      <p>
        The vendored driver <a href="#r4">[4]</a> carries four defects that
        matter for anyone extending this system. None affects the flights that
        were flown, which is the point: they are latent, and each is triggered by
        an ordinary next step.
      </p>
      <ol>
        <li>
          <strong>Shutdown raises.</strong> <code>main()</code> calls{' '}
          <code>drone.cb_shutdown()</code>, which is not defined on the node
          class, since the method that ends the SDK session is called{' '}
          <code>terminate</code>. Any clean shutdown path therefore raises{' '}
          <code>AttributeError</code>.
        </li>
        <li>
          <strong>Threads never stop.</strong> The video, telemetry and odometry
          workers are non-daemon threads running <code>while True</code> with no
          stop condition, so the process does not exit on interrupt.
        </li>
        <li>
          <strong>Images are unstamped.</strong> The image publisher sets{' '}
          <code>header.frame_id</code> but never <code>header.stamp</code>, so
          every frame carries time zero. Nothing downstream can order frames,
          synchronise them against telemetry, or measure the pipeline latency
          that §5.3 identifies as the missing measurement, which is why that
          measurement starts with a one-line fix here.
        </li>
        <li>
          <strong>The camera-info branch cannot run.</strong> The calibration is
          parsed with <code>yaml.load</code>, which returns a dictionary, and
          then read by attribute (<code>self.camera_info.image_height</code>);
          the message fields are also addressed with the ROS&nbsp;1 uppercase
          names (<code>msg.D</code>, <code>msg.K</code>), which in ROS&nbsp;2 are
          lowercase. Either raises. The branch is guarded by a subscriber count
          and the telemetry loop has no exception handling, so the first node to
          subscribe to <code>/camera_info</code> silently kills the thread that
          also publishes <code>/status</code>, <code>/battery</code>,{' '}
          <code>/temperature</code> and <code>/id</code>. Nothing subscribed
          during this project, which is why it was never seen, and §4.4&rsquo;s
          recommendation to use the calibration is exactly what would trigger it.
        </li>
      </ol>
      <p>
        The telemetry the driver does publish should also be read for what it is.{' '}
        <code>Odometry</code> messages carry orientation and body velocities but
        never a position: the pose translation is never written, no{' '}
        <code>child_frame_id</code> is set, and the transform broadcast carries
        only barometric altitude. There is no position estimate anywhere in this
        system, in any frame. Every behaviour is either teleoperated or servoed
        on the current image, and none could be otherwise.
      </p>

      <h3>7.5 The stop marker</h3>
      <p>
        The <code>stop</code> marker is mapped to the SDK{' '}
        <code>emergency</code> command, which cuts power to all four motors. In
        the air that is not a stop; it is a fall. The same command is correctly
        bound to a dedicated kill-switch button in the teleoperation node, where
        an operator invokes it deliberately, but a fiducial marker is a different
        kind of trigger: it fires on a decode, from whatever happens to enter the
        camera&rsquo;s 55&deg; cone, including a marker in the background, a
        printed sheet held by a bystander, or a false positive.
      </p>
      <p>
        A marker named <code>stop</code> should command a zero twist and, if the
        intent is to end the mission, a landing. The distinction between
        &ldquo;cease translating&rdquo; and &ldquo;cease flying&rdquo; costs one
        line and is the clearest safety finding in this review.
      </p>

      <h2 id="s8">8. Results</h2>
      <p>
        The following were demonstrated on the physical aircraft and are visible
        in the video:
      </p>
      <ul>
        <li>
          Teleoperated flight from an Xbox controller, covering takeoff,
          landing, the four translational and rotational axes, and the SDK flip
          primitives, over ROS&nbsp;2, with the live camera feed on the operator station.
        </li>
        <li>
          Marker servoing: the aircraft holds station on a hand-held QR code and
          tracks it laterally, vertically and in range, hovering when the marker
          leaves the frame.
        </li>
        <li>
          Monitoring: a stationary 360&deg; yaw sweep.
        </li>
        <li>
          Travelling: a lateral traverse initiated by the <code>start</code>{' '}
          marker and terminated by <code>finish</code>, when entered directly.
        </li>
        <li>
          Runtime mode switching between all four behaviours from the joystick,
          with the arbiter selecting among four live command sources.
        </li>
        <li>
          Integration of a camera calibration for the platform, shipped with
          the vendored driver (§3.1) rather than produced here, published on{' '}
          <code>/camera_info</code> in the standard ROS format.
        </li>
      </ul>
      <p>
        One defect is reported as unresolved, the monitoring-to-travelling
        transition of §7.1, and this write-up gives its root cause and two
        fixes.
      </p>
      <p>
        What the repository does not contain is any quantitative flight data: no
        end-to-end latency measurement, no step response, no station-keeping
        error distribution, no measurement of the SDK velocity unit in metres per
        second, no maximum tracking speed, and no detection-rate-versus-range
        curve for the markers. Every performance figure in §4 and §5 is derived
        from the calibration and the source constants, not measured. That is the
        honest boundary of this report: the geometry of the system is fully
        characterised, its dynamics are not characterised at all.
      </p>

      <h2 id="s9">9. Limitations and further work</h2>
      <ol>
        <li>
          <strong>Recover full marker pose.</strong> Using the four corner points
          the decoder already returns, instead of the axis-aligned bounding box,
          removes both range biases of §5.5 and supplies the relative bearing
          needed to drive the unused yaw channel. No new sensor, no new
          dependency, and it is the highest-value change available.
        </li>
        <li>
          <strong>Adopt a proper multiplexer contract.</strong> A rate, a timeout
          and a defined idle output per input closes §7.1 and §7.2 together, and
          replaces a bespoke gate with a pattern that is already standard{' '}
          <a href="#r10">[10]</a>.
        </li>
        <li>
          <strong>Measure the loop.</strong> Timestamp the images (§7.4), then
          measure end-to-end latency and the relay&rsquo;s limit cycle. Until the
          plant gain and the loop delay are known, every threshold in §5.3 is a
          tuned constant with no model behind it.
        </li>
        <li>
          <strong>Close the calibration loop.</strong> Servo to the principal
          point rather than the array centre, removing the 1.3&deg;/2.3&deg;
          bias of §4.4, and re-run the calibration with better corner coverage,
          given the distortion budget of §4.3.
        </li>
        <li>
          <strong>Make the stop marker safe.</strong> §7.5.
        </li>
        <li>
          <strong>Add a proportional term.</strong> Once the loop is measured, the
          relay can become a proportional law with saturation, trading the limit
          cycle for a settling time, and the dead band can be specified in
          metres and converted to pixels per frame, removing the asymmetry of
          §5.4.
        </li>
      </ol>

      <h2 id="s10">10. Reproducing the system</h2>
      <p>
        ROS&nbsp;2 Humble on Ubuntu&nbsp;22.04, with the marker decoder and the
        joystick driver from apt:
      </p>
      <pre><code>{`sudo apt install ros-humble-joy libzbar-dev
pip install -r requirements.txt   # av, numpy, djitellopy, imutils`}</code></pre>
      <p>Build the workspace and source it:</p>
      <pre><code>{`colcon build --symlink-install
source install/setup.bash`}</code></pre>
      <p>
        Associate the control station with the drone&rsquo;s access point, then
        bring the system up. The launch file starts the driver, the four
        behaviour nodes, the joystick and the marker decoder; the arbiter is not
        in the launch file and is started separately:
      </p>
      <pre><code>{`ros2 launch drone_control tello_control_launch.py
ros2 run drone_control tello_behavior`}</code></pre>
      <p>
        <code>START</code> arms takeoff, <code>SELECT</code> lands,{' '}
        <code>LB</code> and <code>RB</code> step through the four modes, and the{' '}
        <code>XBOX</code> button is the kill switch. Fly in open space, clear of
        people, with eye protection: the aircraft has no obstacle sensing in any
        direction and, in three of its four modes, no notion of where it is.
      </p>

      <h2>Acknowledgements</h2>
      <p>
        This project rests on tentone&rsquo;s ROS&nbsp;2 Tello driver{' '}
        <a href="#r4">[4]</a>, which supplied the SDK bridge, the message
        definitions and the camera calibration, and on the ZBar library through{' '}
        <code>zbar_ros</code> and <code>pyzbar</code>. Supervised at CPE Lyon.
      </p>

      <h2>References</h2>
      <ol>
        <li id="r1">
          Ryze Tech / DJI, <em>Tello EDU</em>.{' '}
          <a href="https://www.ryzerobotics.com/tello-edu">ryzerobotics.com/tello-edu</a>
        </li>
        <li id="r2">
          Ryze Tech, <em>Tello SDK documentation</em>.{' '}
          <a href="https://www.ryzerobotics.com/tello-edu/downloads">
            ryzerobotics.com/tello-edu/downloads
          </a>
        </li>
        <li id="r3">
          D. Fuentes, <em>DJITelloPy</em>.{' '}
          <a href="https://github.com/damiafuentes/DJITelloPy">
            github.com/damiafuentes/DJITelloPy
          </a>
        </li>
        <li id="r4">
          tentone, <em>tello-ros2</em>.{' '}
          <a href="https://github.com/tentone/tello-ros2">github.com/tentone/tello-ros2</a>
        </li>
        <li id="r5">
          <em>zbar_ros</em>, a ROS wrapper for the ZBar barcode reader.{' '}
          <a href="https://index.ros.org/p/zbar_ros/">index.ros.org/p/zbar_ros</a>
        </li>
        <li id="r6">
          <em>pyzbar</em>.{' '}
          <a href="https://github.com/NaturalHistoryMuseum/pyzbar">
            github.com/NaturalHistoryMuseum/pyzbar
          </a>
        </li>
        <li id="r7">
          <em>REP 103: Standard Units of Measure and Coordinate Conventions</em>.{' '}
          <a href="https://www.ros.org/reps/rep-0103.html">ros.org/reps/rep-0103.html</a>
        </li>
        <li id="r8">
          ISO/IEC 18004, <em>QR Code bar code symbology specification</em>. A
          version-1 symbol is 21 &times; 21 modules.
        </li>
        <li id="r9">
          Project source.{' '}
          <a href={REPO}>github.com/TheoTime01/Tello-drone-with-ROS2</a>
        </li>
        <li id="r10">
          <em>twist_mux</em>, a priority-based twist multiplexer with per-input
          timeouts.{' '}
          <a href="https://github.com/ros-teleop/twist_mux">github.com/ros-teleop/twist_mux</a>
        </li>
      </ol>

      <div className="footnotes">
        <p id="f1">
          <a href="#f1-ref">1.</a> Evaluated by substituting the published
          coefficients into the <code>plumb_bob</code> model at the image corner
          farthest from the principal point. It is an evaluation of the
          calibration, not an independent measurement of the lens.
        </p>
        <p id="f2">
          <a href="#f2-ref">2.</a> The two-to-three pixels per module figure is
          the sampling argument, since a module must be resolved to be read, and is
          consistent with common practice for camera-based QR reading. The actual
          floor for this decoder, this lens and this motion blur was not
          measured; doing so is one afternoon with a tape measure and is the most
          useful single measurement missing from the project.
        </p>
      </div>

      <p className="colophon">
        Every parameter quoted above is read from the source and configuration
        files in the project repository <a href="#r9">[9]</a> rather than
        re-derived: camera intrinsics from the driver&rsquo;s calibration YAML,
        control thresholds and gains from the behaviour nodes, topic wiring from
        the launch file, and the node graph from the <code>rqt_graph</code>{' '}
        capture committed with it. Derived quantities are marked as derived. No
        new experiments were run for this write-up, and the aircraft was not
        available while it was written. Typeset with{' '}
        <a href="https://github.com/vincentdoerig/latex-css">LaTeX.css</a>.
      </p>
    </article>
  );
};

export default TelloProject;
